import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  checkExistingRatings,
  selectPostRating,
} from "../slices/post-product-ratings.slice";

interface RateOrderButtonProps {
  statusId: number;
  view: string | undefined;
  onCloseModal?: () => void;
}

export const RateOrderButton: React.FC<RateOrderButtonProps> = ({
  statusId,
  view,
  onCloseModal,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { orderId } = useParams<{ orderId: string }>();

  // Get state from Redux
  const postRatingState = useAppSelector(selectPostRating);
  const { hasExistingRatings, checkingRatings } = postRatingState;

  // Check for existing ratings when component mounts
  useEffect(() => {
    if (orderId && statusId === 10) {
      dispatch(checkExistingRatings(orderId));
    }
  }, [dispatch, orderId, statusId]);

  const handleRateOrderClick = () => {
    if (hasExistingRatings) {
      return;
    }

    if (orderId && view) {
      const navigateUrl = `/admin/material/order/${orderId}/rate`;
      navigate(navigateUrl);
      if (onCloseModal) {
        onCloseModal();
      }
    }
  };

  // Only render if statusId is 10
  if (statusId !== 10) {
    return null;
  }

  // Determine button state
  let buttonClass =
    "px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors duration-200 ";
  let buttonText = "Rate Order";
  let isDisabled = false;

  if (checkingRatings) {
    buttonClass += "bg-yellow-500 text-white cursor-wait";
    buttonText = "Checking...";
    isDisabled = true;
  } else if (hasExistingRatings) {
    buttonClass += "bg-gray-400 text-gray-600 cursor-not-allowed";
    buttonText = "Already Rated";
    isDisabled = true;
  } else {
    buttonClass +=
      "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500";
    buttonText = "Rate Order";
    isDisabled = false;
  }

  return (
    <button
      onClick={handleRateOrderClick}
      disabled={isDisabled}
      className={buttonClass}
      title={
        hasExistingRatings
          ? "This order has already been rated"
          : "Rate this order"
      }
    >
      {buttonText}
    </button>
  );
};
